"use client";
import React from "react";
import {
  Box,
  Center,
  Separator,
  Flex,
  SimpleGrid,
  Stack,
  VStack,
  Text,
  Table,
} from "@chakra-ui/react";
import { Heading } from "@/elements";
import PricingFooter, { TableFooterProps } from "@/components/pricingFooter";
import { useHeadingVariant } from "@/theme/hooks/useSectionStyle";

type PricingTableData = {
  cpu: string;
  memory: string;
  storage: string;
  bandwidth: string;
  monthlyPrice: number;
};

type PricingTableProps = {
  heading: string;
  data: PricingTableData[];
  footer?: TableFooterProps;
};

export const DesktopView = ({ data }: { data: PricingTableData[] }) => {
  return (
    <Box overflowX="auto" w="100%">
      <Table.Root striped border="2px solid rgb(237,242,247)" width="100%">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader textAlign="center" textTransform="none">
              vCPUs
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Memory</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Bandwidth</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Storage (NVME)</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">
              Monthly Price (USD)
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {data.map((item, index) => (
            <Table.Row key={index}>
              <Table.Cell textAlign="center">{item.cpu}</Table.Cell>
              <Table.Cell textAlign="center">{item.memory}</Table.Cell>
              <Table.Cell textAlign="center">{item.bandwidth}</Table.Cell>
              <Table.Cell textAlign="center">{item.storage}</Table.Cell>
              <Table.Cell
                textAlign="center"
                fontWeight="700"
                fontSize="18px"
                color="primary.200"
              >
                ${item.monthlyPrice.toFixed(2)}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const MobileView = ({ data }: { data: PricingTableData[] }) => {
  return (
    <SimpleGrid columns={2} gap={6}>
      {data.map((plan, index) => (
        <VStack
          key={index}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="gray.200"
          boxShadow="md"
          gap={4}
          w="100%"
          p={4}
          bg="white"
        >
          <Flex direction="column" w="full">
            <Text fontWeight="bold" fontSize="lg">
              {plan.cpu} vCPUs
            </Text>
          </Flex>

          <Separator borderColor="gray.200" />

          <Stack gap={2} w="full">
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Memory:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.memory}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Storage:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.storage}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Bandwidth:{" "}
              </Text>
              <Text as="span" fontWeight="medium" fontSize="md">
                {plan.bandwidth}
              </Text>
            </Box>
            <Box>
              <Text as="span" fontSize="sm" color="text.100">
                Monthly Price:{" "}
              </Text>
              <Text as="span" fontWeight="bold" fontSize="lg" color="primary.200">
                ${plan.monthlyPrice.toFixed(2)}
              </Text>
            </Box>
          </Stack>
        </VStack>
      ))}
    </SimpleGrid>
  );
};

const VmPricingTable = (props: PricingTableProps) => {
  const { heading, data, footer } = props;
  const HeadingVariant = useHeadingVariant();
  return (
    <Box id="pricing-table-section">
      <Heading variant={HeadingVariant} textAlign="center" mb={8}>
        {heading}
      </Heading>
      <Center display={{ md: "none" }} w="full">
        <MobileView data={data} />
      </Center>
      <Box display={{ base: "none", md: "block" }}>
        <DesktopView data={data} />
      </Box>
      {footer && <PricingFooter {...footer} />}
      <Separator borderWidth="2px" borderColor="#ECECEC" my={10} />
    </Box>
  );
};

export default VmPricingTable;
